// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

/**
 * LegalStatusForm Component
 *
 * This component renders a form for selecting the legal status of a product.
 * It includes a select input for choosing the legal status.
 *
 * @component
 * @param {Object} data - The data object containing product information.
 * * @returns {JSX.Element} The rendered LegalStatusForm component.
 */
const LegalStatusForm = ({ data }) => {
    const form = useDialogsForm();
    const { legalStatusOptions } = useSelector((state) => state.products);

    // Function to handle the change event of the select input
    const handleChange = (value) => {
        form.setValue('legalStatus', value);
    };

    // Function to load options for the select input
    const defaultOption = legalStatusOptions.find((option) => option.value === data.legalStatusId);

    return (
        <div className="flexBox">
            <Typography variant="T20" bold>
                Legal Status:
            </Typography>
            <Select
                name="legalStatusId"
                options={legalStatusOptions}
                defaultValue={defaultOption}
                value={form.state.formData?.legalStatus}
                onChange={handleChange}
                useMenuPortal
            />
        </div>
    );
};

/**
 * useLegalStatusDialog Hook
 *
 * This hook provides functionality to open a legal status selection dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openLegalStatusDialog function.
 */
export const useLegalStatusDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => () => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the legal status dialog
    const openLegalStatusDialog = useCallback(
        (data, onConfirm) =>
            openDialog({
                title: `${data.title} (${data.productId})`,
                content: <LegalStatusForm data={data} />,
                closeAction: true,
                primaryAction: {
                    label: 'Save',
                    action: handleSubmit(onConfirm),
                },
            }),
        [openDialog, handleSubmit],
    );

    return { openLegalStatusDialog };
};
